import { TFunction } from 'i18next'
import { DefaultValues } from 'react-hook-form'
import { z } from 'zod'

import { Trainer, TrainerServicePayload } from '@/api/trainers'
import { SelectOption } from '@/components/ui/select'
import { ZIP_CODE_CA_REGEX, ZIP_CODE_US_REGEX } from '@/constants/regex'
import { formatDollars } from '@/utils/currency'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { isValidPhoneInput } from '../../shared/person-table.utils'
import { getTrainerServices } from '../staff.utils'

export const TRAINER_NAME_MAX_LENGTH = 50
export const TRAINER_STREET_MAX_LENGTH = 100
export const TRAINER_APARTMENT_MAX_LENGTH = 20
// Note: the longest accepted value is a ZIP+4 (12345-6789)
export const TRAINER_ZIP_CODE_MAX_LENGTH = 10
export const MAX_PERCENTAGE_FEE = 100

export type TrainerFormMode = 'create' | 'edit'

export const TrainerGenderSchema = z.enum(['Male', 'Female'])

export const ServiceFeeTypeSchema = z.enum(['percentage', 'rates'])

export type ServiceFeeType = z.infer<typeof ServiceFeeTypeSchema>

export interface TrainerServiceRow {
  fee: string
  name: string
  type: ServiceFeeType
}

interface TrainerFormSchemaOptions {
  // Note: BE ignores an empty services list on update, so a trainer that already has services cannot clear them
  requireServices?: boolean
}

export const makeTrainerFormSchema = (
  t: TFunction<['organisation']>,
  mode: TrainerFormMode,
  { requireServices = false }: TrainerFormSchemaOptions = {},
) =>
  z.object({
    active: z.boolean(),
    apartment: z
      .string()
      .max(
        TRAINER_APARTMENT_MAX_LENGTH,
        t('organisation:staff.form.validation.maxLength', { max: TRAINER_APARTMENT_MAX_LENGTH }),
      )
      .optional(),
    avatar: z.array(z.instanceof(File)).optional(),
    email: z
      .string()
      .trim()
      .min(1, t('organisation:staff.form.validation.emailRequired'))
      .refine(
        (value) => mode !== 'create' || !value || z.email().safeParse(value).success,
        t('organisation:staff.form.validation.emailInvalid'),
      ),
    firstName: z
      .string()
      .trim()
      .min(1, t('organisation:staff.form.validation.firstNameRequired'))
      .max(
        TRAINER_NAME_MAX_LENGTH,
        t('organisation:staff.form.validation.maxLength', { max: TRAINER_NAME_MAX_LENGTH }),
      ),
    gender: z.enum(TrainerGenderSchema.options, {
      message: t('organisation:staff.form.validation.genderRequired'),
    }),
    lastName: z
      .string()
      .trim()
      .min(1, t('organisation:staff.form.validation.lastNameRequired'))
      .max(
        TRAINER_NAME_MAX_LENGTH,
        t('organisation:staff.form.validation.maxLength', { max: TRAINER_NAME_MAX_LENGTH }),
      ),
    phone: z
      .string()
      .min(1, t('organisation:staff.form.validation.phoneRequired'))
      .refine(isValidPhoneInput, t('organisation:staff.form.validation.phoneInvalid')),
    // Note: checked on the array (not the whole form) - an object-level refinement is skipped
    // while any field has a type error (e.g. gender not picked yet), hiding these messages.
    services: z
      .array(
        z.object({
          fee: z.string().min(1, t('organisation:staff.form.validation.feeRequired')),
          name: z.string().min(1, t('organisation:staff.form.validation.serviceRequired')),
          type: ServiceFeeTypeSchema,
        }),
      )
      .superRefine((services, ctx) => {
        const seenServices = new Set<string>()
        services.forEach((service, index) => {
          if (service.name && seenServices.has(service.name)) {
            ctx.addIssue({
              code: 'custom',
              message: t('organisation:staff.form.validation.serviceDuplicate'),
              path: [index, 'name'],
            })
          }
          seenServices.add(service.name)

          if (service.type === 'percentage' && Number(service.fee) > MAX_PERCENTAGE_FEE) {
            ctx.addIssue({
              code: 'custom',
              message: t('organisation:staff.form.validation.percentageMax', {
                max: MAX_PERCENTAGE_FEE,
              }),
              path: [index, 'fee'],
            })
          }
        })

        if (requireServices && services.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t('organisation:staff.form.validation.servicesRequired'),
            path: [],
          })
        }
      }),
    street: z
      .string()
      .max(
        TRAINER_STREET_MAX_LENGTH,
        t('organisation:staff.form.validation.maxLength', { max: TRAINER_STREET_MAX_LENGTH }),
      )
      .optional(),
    // Note: same US ZIP / Canadian postal code rule as the clients profile - optional here, validated once filled in
    zipCode: z
      .string()
      .trim()
      .refine(
        (value) => !value || ZIP_CODE_US_REGEX.test(value) || ZIP_CODE_CA_REGEX.test(value),
        t('organisation:staff.form.validation.zipCodeInvalid'),
      )
      .optional(),
  })

export type TrainerFormData = z.infer<ReturnType<typeof makeTrainerFormSchema>>

export const getTrainerFormDefaultValues = (trainer?: Trainer): DefaultValues<TrainerFormData> => ({
  active: trainer?.active ?? true,
  apartment: trainer?.apartment ?? '',
  avatar: [],
  email: trainer?.user.email ?? '',
  firstName: trainer?.user.firstName ?? '',
  gender: trainer?.gender === 'Male' || trainer?.gender === 'Female' ? trainer.gender : undefined,
  lastName: trainer?.user.lastName ?? '',
  phone: formatPhoneNumberDisplay(trainer?.phone),
  services: (trainer ? getTrainerServices(trainer) : []).map((service) => {
    const type: ServiceFeeType = service.percentage ? 'percentage' : 'rates'
    return { fee: formatServiceFee(String(service.fee), type), name: service.serviceId.name, type }
  }),
  street: trainer?.street ?? '',
  zipCode: trainer?.zipCode ?? '',
})

export const SERVICE_FEE_SYMBOLS: Record<ServiceFeeType, string> = {
  percentage: '%',
  rates: '$',
}

export const SERVICE_FEE_FORMATTERS: Record<ServiceFeeType, (fee: string) => string> = {
  percentage: (fee) => `${fee}%`,
  rates: (fee) => formatDollars(fee, { zeroPrice: true }),
}

// Note: a flat rate always shows two decimals (15.00), a percentage drops trailing zeros and is capped at 100
export const formatServiceFee = (value: string | null | undefined, type: ServiceFeeType) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) return value ?? ''

  const amount = Number(value)
  return type === 'rates' ? amount.toFixed(2) : String(Math.min(amount, MAX_PERCENTAGE_FEE))
}

export const toServicePayload = (services: TrainerServiceRow[]): TrainerServicePayload[] =>
  services.map((service) => ({
    fee: Number(service.fee),
    percentage: service.type === 'percentage' ? 1 : 0,
    serviceId: service.name,
  }))

export const getServiceTypeOptions = (
  t: TFunction<['organisation']>,
): SelectOption<ServiceFeeType>[] => [
  { label: t('organisation:staff.form.serviceType.percentage'), value: 'percentage' },
  { label: t('organisation:staff.form.serviceType.rates'), value: 'rates' },
]

export const getGenderOptions = (
  t: TFunction<['organisation']>,
): SelectOption<'Male' | 'Female'>[] => [
  { label: t('organisation:staff.form.gender.male'), value: 'Male' },
  { label: t('organisation:staff.form.gender.female'), value: 'Female' },
]
